// src/components/PortalAfiliado/ListaAlunos.tsx
// UPDATED: 2026-07-19
// ✅ FIX B1: estado do diploma atualiza imediatamente após solicitar (sem fechar modal)
// ✅ FIX B2: datas exibidas sem shift de timezone (+1 dia) via formatDate()
// ✅ FIX F6: botão Editar (lápis) separado na linha da tabela — olhinho só exibe dados

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Users, Search, Filter, Eye, CheckCircle, XCircle, Clock,
  Mail, CreditCard, Calendar, Download, Plus, UserCheck,
  BookOpen, Building2, Loader2, AlertCircle, RefreshCw,
  ShieldOff, ShieldCheck, FileText, GraduationCap,
  Trash2, Camera, Upload, X, Pencil,
} from 'lucide-react';
import { studentService, Student } from '../../services/studentService';
import apiClient from '../../services/api';
import { API_BASE } from '../../utils/apiConfig';

interface RootState {
  auth: { user?: { affiliateId?: string } };
}

// ─────────────────────────────────────────────────────────────────────────────
// FIX B2: formata string de data (YYYY-MM-DD ou ISO) sem conversão de timezone.
// new Date('2025-06-10') é interpretado como UTC midnight → UTC-3 vira 09/06.
// Extraindo os segmentos diretamente evitamos o problema.
// ─────────────────────────────────────────────────────────────────────────────
const formatDate = (value: string | Date | null | undefined): string | undefined => {
  if (!value) return undefined;
  const str = value instanceof Date ? value.toISOString() : String(value);
  const part = str.slice(0, 10); // "YYYY-MM-DD"
  if (!/^\d{4}-\d{2}-\d{2}$/.test(part)) return undefined;
  const [y, m, d] = part.split('-');
  return `${d}/${m}/${y}`;
};

const statusCfg = {
  active:    { label: 'Ativo',    bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: '#22c55e', icon: CheckCircle },
  inactive:  { label: 'Inativo',  bg: 'bg-red-50',     text: 'text-red-600',     border: 'border-red-200',     dot: '#ef4444', icon: XCircle     },
  suspended: { label: 'Suspenso', bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   dot: '#f59e0b', icon: Clock       },
  graduated: { label: 'Formado',  bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    dot: '#3b82f6', icon: CheckCircle  },
} as const;
type StatusKey = keyof typeof statusCfg;

const StudentAvatar: React.FC<{ student: Student; size?: 'sm' | 'md' | 'lg'; className?: string }> = ({ student, size = 'sm', className = '' }) => {
  const [imgErr, setImgErr] = useState(false);
  const sz = size === 'sm' ? 'w-9 h-9 text-xs' : size === 'md' ? 'w-12 h-12 text-sm' : 'w-20 h-20 text-base';
  const initials = student.fullName?.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase() ?? '?';
  return student.photoUrl && !imgErr ? (
    <img key={student.photoUrl} src={student.photoUrl} alt={student.fullName}
      className={`${sz} rounded-full object-cover border-2 border-white flex-shrink-0 ${className}`}
      onError={() => setImgErr(true)} />
  ) : (
    <div className={`${sz} rounded-full bg-gradient-to-br from-gold-300 to-gold-600 flex items-center justify-center text-cosmos-900 font-semibold flex-shrink-0 ${className}`}>
      {initials}
    </div>
  );
};

const ListaAlunos: React.FC<{ onCadastrarClick?: () => void }> = ({ onCadastrarClick }) => {
  const affiliateId = useSelector((state: RootState) => state.auth.user?.affiliateId ?? '');

  const [students, setStudents]               = useState<Student[]>([]);
  const [total, setTotal]                     = useState(0);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState<string | null>(null);
  const [searchTerm, setSearchTerm]           = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter]       = useState<string>('all');
  const [summary, setSummary]                 = useState({ total: 0, ativos: 0, inativos: 0, formados: 0 });
  const [page, setPage]                       = useState(1);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [modalTab, setModalTab]               = useState<'dados' | 'documentos' | 'editar'>('dados');
  const [atualizando, setAtualizando]         = useState(false);
  const [solicitando, setSolicitando]         = useState<string | null>(null);

  // ── Estados edição ────────────────────────────────────────────────────────
  const [formEdit, setFormEdit] = useState<{
    fullName: string; email: string; phone: string; whatsapp: string;
    cpf: string; cin: string; birthdate: string;
    courseName: string; courseWorkload: string;
    courseStartDate: string; courseEndDate: string;
  }>({
    fullName: '', email: '', phone: '', whatsapp: '',
    cpf: '', cin: '', birthdate: '',
    courseName: '', courseWorkload: '',
    courseStartDate: '', courseEndDate: '',
  });
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);
  const [erroEdicao, setErroEdicao]         = useState<string | null>(null);
  const [sucessoEdicao, setSucessoEdicao]   = useState(false);
  const [excluindo, setExcluindo]           = useState(false);

  // ── Estados upload de foto ────────────────────────────────────────────────
  const fileInputRef                          = useRef<HTMLInputElement>(null);
  const [photoPreview, setPhotoPreview]       = useState<string | null>(null);
  const [photoFile, setPhotoFile]             = useState<File | null>(null);
  const [uploadandoFoto, setUploadandoFoto]   = useState(false);
  const [erroFoto, setErroFoto]               = useState<string | null>(null);
  const [sucessoFoto, setSucessoFoto]         = useState(false);

  const limit = 20;

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchTerm); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchStudents = useCallback(async () => {
    if (!affiliateId) {
      setLoading(false);
      setError('Não foi possível identificar o filiado desta sessão. Entre novamente.');
      return;
    }
    setLoading(true); setError(null);
    try {
      const status = statusFilter !== 'all' ? statusFilter : undefined;
      const res: any = debouncedSearch.trim()
        ? await apiClient.get(`/students/search?${new URLSearchParams({
            query: debouncedSearch.trim(),
            page: String(page),
            limit: String(limit),
            affiliateId,
            ...(status ? { status } : {}),
          })}`)
        : await studentService.listStudents({ affiliateId, page, limit, status });
      setStudents(res.students ?? []);
      setTotal(res.total ?? 0);
    } catch (err: any) {
      setError(err?.message ?? 'Erro ao carregar alunos');
    } finally { setLoading(false); }
  }, [affiliateId, page, statusFilter, debouncedSearch]);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  useEffect(() => {
    if (!affiliateId) return;
    apiClient.get(`/students/stats/summary?affiliateId=${encodeURIComponent(affiliateId)}`)
      .then((data: any) => setSummary({
        total: data.total ?? 0,
        ativos: data.active ?? 0,
        inativos: data.inactive ?? 0,
        formados: data.graduated ?? 0,
      }))
      .catch(() => setSummary({ total: 0, ativos: 0, inativos: 0, formados: 0 }));
  }, [affiliateId]);

  const filtered = useMemo(() => {
    return students;
  }, [students]);

  const stats = summary;

  const totalPages = Math.ceil(total / limit);

  const handleToggleStatus = async (student: Student) => {
    const novoStatus = student.status === 'active' ? 'inactive' : 'active';
    const msg = novoStatus === 'inactive'
      ? `Desativar a conta de ${student.fullName}? O aluno perderá acesso à plataforma.`
      : `Reativar a conta de ${student.fullName}?`;
    if (!confirm(msg)) return;
    setAtualizando(true);
    try {
      await apiClient.patch(`/students/${student.id}`, { status: novoStatus });
      setStudents(prev => prev.map(s => s.id === student.id ? { ...s, status: novoStatus as any } : s));
      setSelectedStudent(prev => prev?.id === student.id ? { ...prev, status: novoStatus as any } : prev);
    } catch (err: any) {
      alert(err?.response?.data?.message ?? err?.message ?? 'Erro ao atualizar status');
    } finally { setAtualizando(false); }
  };

  // ── Solicitar Diploma ─────────────────────────────────────────────────────
  // FIX B1: após sucesso, cria o objeto pending e atualiza selectedStudent + students
  // imediatamente — sem precisar fechar e reabrir o modal.
  const handleSolicitarDiploma = async (student: Student) => {
    if (!confirm(`Solicitar diploma para ${student.fullName}? A federação será notificada.`)) return;
    setSolicitando(student.id);
    try {
      const res = await fetch(`${API_BASE}/students/${student.id}/request-diploma`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token') ?? ''}`,
        },
        body: JSON.stringify({ type: 'diploma' }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        throw new Error(err.message ?? 'Erro ao solicitar diploma');
      }

      // Tenta usar o objeto retornado pelo backend; se não vier, monta um local.
      const body = await res.json().catch(() => ({}));
      const newRequest = {
        id:          body?.id          ?? `local-${Date.now()}`,
        type:        'diploma'  as const,
        status:      'pending'  as const,
        requestedAt: body?.requestedAt ?? new Date().toISOString(),
      };

      const existing  = ((student as any).diplomaRequests ?? []) as any[];
      const updatedStudent = {
        ...student,
        diplomaRequests: [...existing, newRequest],
      } as Student;

      // Atualiza o modal e a lista sem fechar nada
      setSelectedStudent(updatedStudent);
      setStudents(prev => prev.map(s => s.id === student.id ? updatedStudent : s));

      alert(`✅ Diploma solicitado para ${student.fullName}! A federação irá processar em breve.`);
    } catch (err: any) {
      alert(`Erro: ${err.message}`);
    } finally { setSolicitando(null); }
  };

  // ── Abrir modal na aba Editar (direto) ───────────────────────────────────
  const abrirEditar = (student: Student) => {
    setFormEdit({
      fullName:        student.fullName        ?? '',
      email:           student.email           ?? '',
      phone:           student.phone           ?? '',
      whatsapp:        (student as any).whatsapp ?? '',
      cpf:             student.cpf?.replace(/\D/g, '') ?? '',
      cin:             student.cin             ?? '',
      birthdate:       student.birthdate ? String(student.birthdate).slice(0, 10) : '',
      courseName:      student.courseName      ?? '',
      courseWorkload:  student.courseWorkload  ? String(student.courseWorkload) : '',
      courseStartDate: student.courseStartDate ? String(student.courseStartDate).slice(0, 10) : '',
      courseEndDate:   student.courseEndDate   ? String(student.courseEndDate).slice(0, 10)   : '',
    });
    setErroEdicao(null);
    setSucessoEdicao(false);
    setPhotoPreview(null);
    setPhotoFile(null);
    setErroFoto(null);
    setSucessoFoto(false);
  };

  // FIX F6: abre modal direto na aba editar a partir da linha da tabela
  const handleAbrirModalEditar = (student: Student) => {
    setSelectedStudent(student);
    setModalTab('editar');
    abrirEditar(student);
  };

  // ── Selecionar foto ───────────────────────────────────────────────────────
  const handleSelecionarFoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErroFoto('Selecione um arquivo de imagem (JPG, PNG, WebP).');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErroFoto('A imagem deve ter no máximo 5 MB.');
      return;
    }
    setErroFoto(null);
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = ev => setPhotoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  // ── Upload foto ───────────────────────────────────────────────────────────
  const handleUploadFoto = async () => {
    if (!selectedStudent || !photoFile) return;
    setUploadandoFoto(true);
    setErroFoto(null);
    setSucessoFoto(false);
    try {
      const fd = new FormData();
      fd.append('file', photoFile);

      const uploadRes = await fetch(
        `${API_BASE}/documents/public-upload?affiliateId=${encodeURIComponent(affiliateId)}&documentType=foto&description=Foto%20do%20aluno`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') ?? ''}` },
          body: fd,
        },
      );
      if (!uploadRes.ok) {
        const err = await uploadRes.json().catch(() => ({ message: uploadRes.statusText }));
        throw new Error(err.message ?? 'Erro ao enviar imagem');
      }
      const uploadData = await uploadRes.json();
      const novaUrl: string = uploadData.url ?? uploadData.fileUrl ?? uploadData.path ?? uploadData.signedUrl ?? '';
      if (!novaUrl) throw new Error('URL da foto não retornada pelo servidor');

      const patchRes = await fetch(`${API_BASE}/students/${selectedStudent.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token') ?? ''}`,
        },
        body: JSON.stringify({ photoUrl: novaUrl }),
      });
      if (!patchRes.ok) {
        const err = await patchRes.json().catch(() => ({ message: patchRes.statusText }));
        throw new Error(err.message ?? 'Erro ao salvar foto no aluno');
      }

      const updated: Student = { ...selectedStudent, photoUrl: novaUrl };
      setSelectedStudent(updated);
      setStudents(prev => prev.map(s => s.id === selectedStudent.id ? updated : s));
      setSucessoFoto(true);
      setPhotoFile(null);
      setPhotoPreview(null);
      setTimeout(() => setSucessoFoto(false), 2500);
    } catch (err: any) {
      setErroFoto(err.message ?? 'Erro ao enviar foto');
    } finally { setUploadandoFoto(false); }
  };

  // ── Salvar edição ─────────────────────────────────────────────────────────
  const handleSalvarEdicao = async () => {
    if (!selectedStudent) return;
    setSalvandoEdicao(true); setErroEdicao(null); setSucessoEdicao(false);
    try {
      const payload: Record<string, any> = {};
      if (formEdit.fullName.trim())                    payload.fullName        = formEdit.fullName.trim();
      if (formEdit.email.trim())                       payload.email           = formEdit.email.trim();
      if (formEdit.phone.trim())                       payload.phone           = formEdit.phone.trim();
      if (formEdit.whatsapp.trim())                    payload.whatsapp        = formEdit.whatsapp.trim();
      if (formEdit.cpf.replace(/\D/g, ''))             payload.cpf             = formEdit.cpf.replace(/\D/g, '');
      if (formEdit.cin.trim())                         payload.cin             = formEdit.cin.trim();
      if (formEdit.birthdate)                          payload.birthdate       = formEdit.birthdate;
      if (formEdit.courseName.trim())                  payload.courseName      = formEdit.courseName.trim();
      if (formEdit.courseWorkload)                     payload.courseWorkload  = Number(formEdit.courseWorkload);
      if (formEdit.courseStartDate)                    payload.courseStartDate = formEdit.courseStartDate;
      if (formEdit.courseEndDate)                      payload.courseEndDate   = formEdit.courseEndDate;

      await apiClient.patch(`/students/${selectedStudent.id}`, payload);

      const updated: Student = { ...selectedStudent, ...payload };
      setSelectedStudent(updated);
      setStudents(prev => prev.map(s => s.id === selectedStudent.id ? updated : s));
      setSucessoEdicao(true);
      setTimeout(() => { setSucessoEdicao(false); setModalTab('dados'); }, 1800);
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      setErroEdicao(Array.isArray(msg) ? msg.join(', ') : (msg ?? err.message ?? 'Erro ao salvar'));
    } finally { setSalvandoEdicao(false); }
  };

  // ── Excluir aluno ─────────────────────────────────────────────────────────
  const handleExcluirAluno = async () => {
    if (!selectedStudent) return;
    if (!confirm(`⚠️ Tem certeza que deseja excluir o aluno "${selectedStudent.fullName}"?\n\nEsta ação não pode ser desfeita.`)) return;
    if (!confirm(`Confirmação final: excluir permanentemente "${selectedStudent.fullName}"?`)) return;

    setExcluindo(true);
    setErroEdicao(null);
    try {
      const res = await fetch(`${API_BASE}/students/${selectedStudent.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token') ?? ''}`,
        },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        throw new Error(err.message ?? 'Erro ao excluir aluno');
      }
      setStudents(prev => prev.filter(s => s.id !== selectedStudent.id));
      setTotal(prev => Math.max(0, prev - 1));
      setSelectedStudent(null);
    } catch (err: any) {
      setErroEdicao(err.message ?? 'Erro ao excluir aluno');
    } finally { setExcluindo(false); }
  };

  if (loading && students.length === 0) return (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <Loader2 className="w-8 h-8 text-gold-500 animate-spin"/>
      <p className="text-sm text-gray-400">Carregando alunos…</p>
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
      <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center">
        <AlertCircle className="w-6 h-6 text-red-400"/>
      </div>
      <p className="text-sm font-medium text-gray-700">Erro ao carregar alunos</p>
      <p className="text-xs text-gray-400">{error}</p>
      <button onClick={fetchStudents} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm border border-gray-200 text-gray-600 hover:bg-gray-50">
        <RefreshCw className="w-4 h-4"/> Tentar novamente
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="relative flex-shrink-0">
            <div className="absolute inset-0 bg-gold-400/20 blur-[10px] rounded-full"/>
            <img src="/icons/alu.png" alt="" className="relative w-11 h-11 object-contain drop-shadow"/>
          </div>
          <div>
            <h1 className="text-[22px] font-semibold text-gray-900 tracking-tight leading-none">Meus Alunos</h1>
            <p className="text-xs text-gray-400 mt-1">{loading ? 'Atualizando…' : `${total} aluno${total !== 1 ? 's' : ''} no total`}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchStudents} disabled={loading}
            className="p-2.5 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}/>
          </button>
          <button onClick={onCadastrarClick}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-cosmos-900"
            style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}>
            <Plus className="w-4 h-4"/> Novo Aluno
          </button>
        </div>
      </div>

      {/* MINI STATS */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total',    value: stats.total,    dot: '6366f1', icon: Users        },
          { label: 'Ativos',   value: stats.ativos,   dot: '22c55e', icon: UserCheck    },
          { label: 'Inativos', value: stats.inativos, dot: 'ef4444', icon: XCircle      },
          { label: 'Formados', value: stats.formados, dot: '3b82f6', icon: GraduationCap },
        ].map(({ label, value, dot, icon: Icon }) => (
          <div key={label} className="bg-white rounded-2xl border border-gray-100 px-4 py-3.5
            shadow-[0_1px_6px_rgba(0,0,0,0.04)] flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: `#${dot}15` }}>
              <Icon className="w-4 h-4" style={{ color: `#${dot}` }}/>
            </div>
            <div>
              <p className="text-lg font-semibold text-gray-900 leading-none">{value}</p>
              <p className="text-[11px] text-gray-400 mt-0.5">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* FILTROS */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Buscar por nome, e-mail, CPF ou ID…"
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-transparent"/>
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"/>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            className="pl-9 pr-8 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold-400 appearance-none cursor-pointer">
            <option value="all">Todos os status</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
            <option value="graduated">Formados</option>
            <option value="suspended">Suspensos</option>
          </select>
        </div>
      </div>

      {/* TABELA */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_1px_8px_rgba(0,0,0,0.05)] overflow-hidden">
        {filtered.length > 0 ? (
          <>
            <div className="hidden lg:grid grid-cols-[1fr_1fr_1fr_160px_auto] gap-4 px-5 py-3 border-b border-gray-100 bg-gray-50/70">
              {['Aluno', 'Contato', 'Escola / Curso', 'Status / Diploma', ''].map(h => (
                <p key={h} className="text-[10.5px] font-semibold text-gray-400 uppercase tracking-wider">{h}</p>
              ))}
            </div>
            <div className="divide-y divide-gray-50">
              {filtered.map(student => {
                const sk = (student.status ?? 'inactive') as StatusKey;
                const s = statusCfg[sk] ?? statusCfg.inactive;
                return (
                  <div key={student.id}
                    className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_160px_auto] gap-3 lg:gap-4 px-5 py-4 hover:bg-gray-50/50 transition-colors items-center">
                    <div className="flex items-center gap-3">
                      <StudentAvatar student={student} size="sm"/>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{student.fullName}</p>
                        <p className="text-[11px] text-gray-400 font-mono">{student.systemId}</p>
                      </div>
                    </div>
                    <div className="space-y-0.5 min-w-0">
                      <p className="text-xs text-gray-600 flex items-center gap-1.5 truncate">
                        <Mail className="w-3 h-3 text-gray-400 flex-shrink-0"/>{student.email || '—'}
                      </p>
                      {(student.cpf || student.cin) && (
                        <p className="text-xs text-gray-500 flex items-center gap-1.5">
                          <CreditCard className="w-3 h-3 text-gray-400 flex-shrink-0"/>{student.cpf || student.cin}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 flex items-center gap-1.5">
                        <Calendar className="w-3 h-3 text-gray-400 flex-shrink-0"/>
                        {formatDate(student.createdAt as any) ?? '—'}
                      </p>
                    </div>
                    <div className="space-y-0.5 min-w-0">
                      <p className="text-xs text-gray-700 flex items-center gap-1.5 truncate font-medium">
                        <Building2 className="w-3 h-3 text-gray-400 flex-shrink-0"/>{student.affiliate?.schoolName || '—'}
                      </p>
                      {student.courseName && (
                        <p className="text-xs text-gray-500 flex items-center gap-1.5 truncate">
                          <BookOpen className="w-3 h-3 text-gray-400 flex-shrink-0"/>
                          {student.courseName}{student.courseWorkload ? ` · ${student.courseWorkload}h` : ''}
                        </p>
                      )}
                    </div>
                    {/* STATUS + ação de diploma na mesma célula */}
                    <div className="flex flex-col gap-1.5">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium border w-fit ${s.bg} ${s.text} ${s.border}`}>
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: s.dot }}/>
                        {s.label}
                      </span>
                      {(() => {
                        const reqs = (student as any).diplomaRequests ?? [];
                        const last = reqs[reqs.length - 1];
                        if (last?.status === 'approved') return (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium border bg-emerald-50 text-emerald-700 border-emerald-200 w-fit">
                            <CheckCircle className="w-3 h-3"/> Diploma Emitido
                          </span>
                        );
                        if (last?.status === 'pending') return (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium border bg-amber-50 text-amber-700 border-amber-200 w-fit">
                            <Clock className="w-3 h-3"/> Em Análise
                          </span>
                        );
                        return (
                          <button
                            onClick={() => handleSolicitarDiploma(student)}
                            disabled={!!solicitando}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold border bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 hover:border-purple-400 disabled:opacity-50 transition-all w-fit">
                            {solicitando === student.id
                              ? <Loader2 className="w-3 h-3 animate-spin"/>
                              : <GraduationCap className="w-3 h-3"/>}
                            {solicitando === student.id ? 'Solicitando…' : 'Solicitar Diploma'}
                          </button>
                        );
                      })()}
                    </div>

                    {/* dois botões: olhinho e lápis */}
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => { setSelectedStudent(student); setModalTab('dados'); }}
                        title="Ver dados"
                        className="p-2 rounded-xl border border-gray-200 text-gray-400 hover:border-gold-300 hover:text-gold-600 hover:bg-gold-50 transition-all">
                        <Eye className="w-4 h-4"/>
                      </button>
                      <button
                        onClick={() => handleAbrirModalEditar(student)}
                        title="Editar aluno"
                        className="p-2 rounded-xl border border-gray-200 text-gray-400 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50 transition-all">
                        <Pencil className="w-4 h-4"/>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            {totalPages > 1 && (
              <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between">
                <p className="text-xs text-gray-400">Página {page} de {totalPages} · {total} alunos</p>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40">Anterior</button>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40">Próxima</button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center px-6">
            <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-gray-300"/>
            </div>
            <p className="text-sm font-medium text-gray-600">Nenhum aluno encontrado</p>
            <p className="text-xs text-gray-400 mt-1">
              {searchTerm || statusFilter !== 'all' ? 'Ajuste os filtros de busca' : 'Comece cadastrando seu primeiro aluno'}
            </p>
            {!searchTerm && statusFilter === 'all' && onCadastrarClick && (
              <button onClick={onCadastrarClick}
                className="mt-4 flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-cosmos-900"
                style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}>
                <Plus className="w-4 h-4"/> Cadastrar primeiro aluno
              </button>
            )}
          </div>
        )}
      </div>

      {/* MODAL DETALHES */}
      {selectedStudent && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedStudent(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl flex flex-col max-h-[90vh]"
            onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0">
              <h3 className="text-base font-semibold text-gray-900">Detalhes do Aluno</h3>
              <button onClick={() => setSelectedStudent(null)}
                className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100">✕</button>
            </div>

            {/* Avatar + nome */}
            <div className="flex items-center gap-4 px-6 pb-4 border-b border-gray-100 flex-shrink-0">
              <StudentAvatar student={selectedStudent} size="lg" className="border-2 border-gold-200 shadow-sm"/>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900">{selectedStudent.fullName}</p>
                <p className="text-xs font-mono text-gold-600">{selectedStudent.systemId}</p>
                {(() => {
                  const sk = (selectedStudent.status ?? 'inactive') as StatusKey;
                  const s = statusCfg[sk] ?? statusCfg.inactive;
                  return (
                    <span className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-md text-[10px] font-medium border ${s.bg} ${s.text} ${s.border}`}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }}/>{s.label}
                    </span>
                  );
                })()}
              </div>
            </div>

            {/* Abas */}
            <div className="flex border-b border-gray-100 px-6 flex-shrink-0">
              {([
                { key: 'dados',      label: 'Dados'      },
                { key: 'documentos', label: 'Documentos' },
                { key: 'editar',     label: 'Editar'     },
              ] as const).map(({ key, label }) => (
                <button key={key}
                  onClick={() => {
                    setModalTab(key);
                    if (key === 'editar') abrirEditar(selectedStudent);
                  }}
                  className={`px-4 py-3 text-xs font-semibold border-b-2 transition-colors ${
                    modalTab === key
                      ? 'border-gold-500 text-gold-700'
                      : 'border-transparent text-gray-400 hover:text-gray-600'
                  }`}>
                  {label}
                </button>
              ))}
            </div>

            {/* Body */}
            <div className="overflow-y-auto flex-1 px-6 py-4 space-y-5">

              {/* ABA DADOS */}
              {modalTab === 'dados' && (
                <>
                  <section>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Identificação</p>
                    <div className="grid grid-cols-2 gap-3 bg-gray-50 rounded-xl p-3">
                      {[
                        { label: 'CPF',        value: selectedStudent.cpf },
                        { label: 'CIN',        value: selectedStudent.cin },
                        // FIX B2: formatDate evita o +1 dia por timezone
                        { label: 'Nascimento', value: formatDate(selectedStudent.birthdate as any) },
                        { label: 'Matrícula',  value: formatDate(selectedStudent.enrollmentDate as any) },
                      ].filter(f => f.value).map(f => (
                        <div key={f.label}>
                          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">{f.label}</p>
                          <p className="text-sm text-gray-700">{f.value}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                  <section>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Contato</p>
                    <div className="grid grid-cols-2 gap-3 bg-gray-50 rounded-xl p-3">
                      {[
                        { label: 'E-mail',   value: selectedStudent.email },
                        { label: 'Telefone', value: selectedStudent.phone },
                        { label: 'WhatsApp', value: (selectedStudent as any).whatsapp },
                      ].filter(f => f.value).map(f => (
                        <div key={f.label}>
                          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">{f.label}</p>
                          <p className="text-sm text-gray-700 break-all">{f.value}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                  {(selectedStudent.courseName || selectedStudent.courseWorkload) && (
                    <section>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Curso</p>
                      <div className="grid grid-cols-2 gap-3 bg-gray-50 rounded-xl p-3">
                        {[
                          { label: 'Curso',   value: selectedStudent.courseName },
                          { label: 'Carga',   value: selectedStudent.courseWorkload ? `${selectedStudent.courseWorkload}h` : undefined },
                          // FIX B2: usa formatDate para todas as datas de curso
                          { label: 'Início',  value: formatDate(selectedStudent.courseStartDate as any) },
                          { label: 'Término', value: formatDate(selectedStudent.courseEndDate as any) },
                          { label: 'Escola',  value: selectedStudent.affiliate?.schoolName },
                        ].filter(f => f.value).map(f => (
                          <div key={f.label}>
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">{f.label}</p>
                            <p className="text-sm text-gray-700">{f.value}</p>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}
                </>
              )}

              {/* ABA DOCUMENTOS */}
              {modalTab === 'documentos' && (
                <section>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">Documentos e Foto</p>
                  {selectedStudent.photoUrl ? (
                    <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-4 border border-gray-100">
                      <img src={selectedStudent.photoUrl} alt={selectedStudent.fullName}
                        className="w-16 h-16 rounded-xl object-cover border-2 border-gray-200 flex-shrink-0"/>
                      <div>
                        <p className="text-xs font-semibold text-gray-700">Foto do Aluno</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">Usada na carteira digital</p>
                        <a href={selectedStudent.photoUrl} target="_blank" rel="noopener noreferrer"
                          className="text-[11px] text-gold-600 hover:text-gold-700 font-medium mt-1 inline-flex items-center gap-1">
                          Abrir em nova aba →
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                      <FileText className="w-8 h-8 text-gray-300 mb-2"/>
                      <p className="text-sm text-gray-500">Nenhum documento cadastrado</p>
                    </div>
                  )}
                </section>
              )}

              {/* ABA EDITAR */}
              {modalTab === 'editar' && (
                <div className="space-y-4">
                  {erroEdicao && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700">
                      <AlertCircle className="w-4 h-4 flex-shrink-0"/>
                      <p className="text-xs font-semibold">{erroEdicao}</p>
                    </div>
                  )}
                  {sucessoEdicao && (
                    <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700">
                      <CheckCircle className="w-4 h-4 flex-shrink-0"/>
                      <p className="text-xs font-semibold">Dados salvos com sucesso!</p>
                    </div>
                  )}

                  {/* ── Foto de perfil ── */}
                  <section>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Foto de Perfil</p>
                    <div className="flex items-center gap-4 bg-gray-50 rounded-xl p-4 border border-gray-100">
                      <div className="relative flex-shrink-0">
                        {photoPreview ? (
                          <img src={photoPreview} alt="Preview"
                            className="w-16 h-16 rounded-xl object-cover border-2 border-gold-200"/>
                        ) : selectedStudent.photoUrl ? (
                          <img src={selectedStudent.photoUrl} alt={selectedStudent.fullName}
                            className="w-16 h-16 rounded-xl object-cover border-2 border-gray-200"/>
                        ) : (
                          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-gold-300 to-gold-600 flex items-center justify-center text-cosmos-900 font-semibold text-lg">
                            {selectedStudent.fullName?.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase() ?? '?'}
                          </div>
                        )}
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="absolute -bottom-1.5 -right-1.5 w-6 h-6 rounded-full bg-gold-500 hover:bg-gold-600 flex items-center justify-center shadow-sm transition-colors">
                          <Camera className="w-3 h-3 text-cosmos-900"/>
                        </button>
                      </div>

                      <div className="flex-1 min-w-0">
                        {photoFile ? (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <p className="text-xs font-medium text-gray-700 truncate flex-1">{photoFile.name}</p>
                              <button onClick={() => { setPhotoFile(null); setPhotoPreview(null); setErroFoto(null); }}
                                className="p-0.5 text-gray-400 hover:text-gray-600">
                                <X className="w-3.5 h-3.5"/>
                              </button>
                            </div>
                            <p className="text-[11px] text-gray-400">{(photoFile.size / 1024).toFixed(0)} KB</p>
                            {erroFoto && <p className="text-[11px] text-red-600 font-medium">{erroFoto}</p>}
                            {sucessoFoto && <p className="text-[11px] text-emerald-600 font-medium">✓ Foto atualizada!</p>}
                            <button onClick={handleUploadFoto} disabled={uploadandoFoto}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white disabled:opacity-50 transition-colors"
                              style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)' }}>
                              {uploadandoFoto
                                ? <><Loader2 className="w-3.5 h-3.5 animate-spin"/> Enviando…</>
                                : <><Upload className="w-3.5 h-3.5"/> Enviar foto</>}
                            </button>
                          </div>
                        ) : (
                          <div>
                            <p className="text-xs font-medium text-gray-700">
                              {selectedStudent.photoUrl ? 'Trocar foto' : 'Adicionar foto'}
                            </p>
                            <p className="text-[11px] text-gray-400 mt-0.5">JPG, PNG ou WebP · máx. 5 MB</p>
                            {erroFoto && <p className="text-[11px] text-red-600 font-medium mt-1">{erroFoto}</p>}
                            {sucessoFoto && <p className="text-[11px] text-emerald-600 font-medium mt-1">✓ Foto atualizada!</p>}
                            <button onClick={() => fileInputRef.current?.click()}
                              className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-white transition-colors">
                              <Camera className="w-3.5 h-3.5"/> Selecionar imagem
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleSelecionarFoto}
                    />
                  </section>

                  {/* Dados pessoais */}
                  <section className="border-t border-gray-100 pt-4">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Dados Pessoais</p>
                    <div className="space-y-2.5">
                      <div>
                        <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Nome Completo</label>
                        <input value={formEdit.fullName}
                          onChange={e => setFormEdit(p => ({ ...p, fullName: e.target.value }))}
                          className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-transparent"
                          placeholder="Nome do aluno"/>
                      </div>
                      <div className="grid grid-cols-2 gap-2.5">
                        <div>
                          <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">CPF</label>
                          <input value={formEdit.cpf}
                            onChange={e => setFormEdit(p => ({ ...p, cpf: e.target.value.replace(/\D/g,'').slice(0,11) }))}
                            className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-transparent"
                            placeholder="Somente números" maxLength={11}/>
                        </div>
                        <div>
                          <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">CIN</label>
                          <input value={formEdit.cin}
                            onChange={e => setFormEdit(p => ({ ...p, cin: e.target.value }))}
                            className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-transparent"
                            placeholder="Nº CIN"/>
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Data de Nascimento</label>
                        <input type="date" value={formEdit.birthdate}
                          onChange={e => setFormEdit(p => ({ ...p, birthdate: e.target.value }))}
                          className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-transparent"/>
                      </div>
                    </div>
                  </section>

                  {/* Contato */}
                  <section className="border-t border-gray-100 pt-4">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Contato</p>
                    <div className="space-y-2.5">
                      <div>
                        <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">E-mail</label>
                        <input type="email" value={formEdit.email}
                          onChange={e => setFormEdit(p => ({ ...p, email: e.target.value }))}
                          className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-transparent"
                          placeholder="email@dominio.com"/>
                      </div>
                      <div className="grid grid-cols-2 gap-2.5">
                        <div>
                          <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Telefone</label>
                          <input value={formEdit.phone}
                            onChange={e => setFormEdit(p => ({ ...p, phone: e.target.value }))}
                            className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-transparent"
                            placeholder="(00) 00000-0000"/>
                        </div>
                        <div>
                          <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">WhatsApp</label>
                          <input value={formEdit.whatsapp}
                            onChange={e => setFormEdit(p => ({ ...p, whatsapp: e.target.value }))}
                            className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-transparent"
                            placeholder="(00) 00000-0000"/>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Curso */}
                  <section className="border-t border-gray-100 pt-4">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Curso</p>
                    <div className="space-y-2.5">
                      <div className="grid grid-cols-2 gap-2.5">
                        <div>
                          <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Nome do Curso</label>
                          <input value={formEdit.courseName}
                            onChange={e => setFormEdit(p => ({ ...p, courseName: e.target.value }))}
                            className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-transparent"
                            placeholder="Ex: Cabeleireiro"/>
                        </div>
                        <div>
                          <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Carga Horária (h)</label>
                          <input type="number" min={0} value={formEdit.courseWorkload}
                            onChange={e => setFormEdit(p => ({ ...p, courseWorkload: e.target.value }))}
                            className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-transparent"
                            placeholder="Ex: 120"/>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2.5">
                        <div>
                          <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Data de Início</label>
                          <input type="date" value={formEdit.courseStartDate}
                            onChange={e => setFormEdit(p => ({ ...p, courseStartDate: e.target.value }))}
                            className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-transparent"/>
                        </div>
                        <div>
                          <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Data de Término</label>
                          <input type="date" value={formEdit.courseEndDate}
                            onChange={e => setFormEdit(p => ({ ...p, courseEndDate: e.target.value }))}
                            className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-transparent"/>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex flex-col gap-2 flex-shrink-0">

              {/* Diploma — só aparece nas abas dados/documentos */}
              {modalTab !== 'editar' && (() => {
                const requests = (selectedStudent as any).diplomaRequests ?? [];
                const lastReq  = requests[requests.length - 1];

                if (lastReq?.status === 'approved') {
                  const roleLabel = lastReq.promotedAs === 'professional' ? 'Profissional'
                                  : lastReq.promotedAs === 'affiliate'    ? 'Filiado'
                                  : null;
                  return (
                    <div className="w-full space-y-2">
                      <div className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl
                        font-medium text-sm bg-emerald-50 border border-emerald-200 text-emerald-700">
                        <CheckCircle className="w-4 h-4"/>
                        Diploma Aprovado
                        {roleLabel && (
                          <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold
                            bg-blue-100 text-blue-700 border border-blue-200">
                            → {roleLabel}
                          </span>
                        )}
                      </div>
                      {(lastReq.generatedId || lastReq.registroFepcssp) && (
                        <div className="bg-gray-50 rounded-xl px-4 py-3 border border-gray-100 space-y-1.5">
                          {lastReq.generatedId && (
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] text-gray-400 font-medium">ID de Registro</span>
                              <span className="text-[11px] font-mono font-bold text-gray-700 bg-white px-2 py-0.5 rounded-lg border border-gray-200">
                                {lastReq.generatedId}
                              </span>
                            </div>
                          )}
                          {lastReq.registroFepcssp && (
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] text-gray-400 font-medium">Registro FEPCSSP</span>
                              <span className="text-[11px] font-mono font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-lg border border-blue-200">
                                #{lastReq.registroFepcssp}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                }

                if (lastReq?.status === 'rejected') return (
                  <div className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl
                    font-medium text-sm bg-red-50 border border-red-200 text-red-700">
                    <XCircle className="w-4 h-4"/> Diploma Recusado
                  </div>
                );

                // FIX B1: status 'pending' agora aparece imediatamente após solicitar
                if (lastReq?.status === 'pending') return (
                  <div className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl
                    font-medium text-sm bg-amber-50 border border-amber-200 text-amber-700">
                    <Clock className="w-4 h-4"/> Diploma em Análise
                  </div>
                );

                return (
                  <button onClick={() => handleSolicitarDiploma(selectedStudent!)} disabled={!!solicitando}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl
                      font-medium text-sm text-white transition-colors disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)' }}>
                    {solicitando === selectedStudent!.id ? <Loader2 className="w-4 h-4 animate-spin"/> : <GraduationCap className="w-4 h-4"/>}
                    {solicitando === selectedStudent!.id ? 'Solicitando…' : 'Solicitar Diploma'}
                  </button>
                );
              })()}

              {/* Botões de ação */}
              <div className="flex gap-2">
                <button onClick={() => setSelectedStudent(null)}
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
                  Fechar
                </button>

                {modalTab === 'editar' ? (
                  <>
                    <button
                      onClick={handleExcluirAluno}
                      disabled={excluindo || salvandoEdicao}
                      title="Excluir aluno permanentemente"
                      className="px-3.5 py-2.5 rounded-xl bg-red-50 border border-red-200 text-red-600
                        hover:bg-red-100 disabled:opacity-50 transition-colors flex items-center gap-1.5
                        text-sm font-medium">
                      {excluindo
                        ? <Loader2 className="w-4 h-4 animate-spin"/>
                        : <Trash2 className="w-4 h-4"/>}
                      {excluindo ? 'Excluindo…' : 'Excluir'}
                    </button>
                    <button onClick={handleSalvarEdicao} disabled={salvandoEdicao || excluindo}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium text-cosmos-900 disabled:opacity-50"
                      style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}>
                      {salvandoEdicao
                        ? <><Loader2 className="w-4 h-4 animate-spin"/> Salvando…</>
                        : <><CheckCircle className="w-4 h-4"/> Salvar Alterações</>}
                    </button>
                  </>
                ) : selectedStudent.status === 'active' ? (
                  <button onClick={() => handleToggleStatus(selectedStudent)} disabled={atualizando}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-medium hover:bg-red-100 disabled:opacity-50">
                    {atualizando ? <Loader2 className="w-4 h-4 animate-spin"/> : <ShieldOff className="w-4 h-4"/>}
                    Desativar
                  </button>
                ) : (
                  <button onClick={() => handleToggleStatus(selectedStudent)} disabled={atualizando}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-medium hover:bg-emerald-100 disabled:opacity-50">
                    {atualizando ? <Loader2 className="w-4 h-4 animate-spin"/> : <ShieldCheck className="w-4 h-4"/>}
                    Reativar
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListaAlunos;
